import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { productService } from '../../services/productService';
import { companyService } from '../../services/companyService';

// Import all components
import ProductsList from './ProductsList';
import ProductDetailsView from './ProductDetailsView';
import ProductFormModal from './ProductFormModal';
import ProductFilters from './ProductFilters';
import { DeleteModal } from '../shared/DeleteModal';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { ErrorAlert } from '../shared/ErrorAlert';

const ProductsContent = ({ companyFilter = null }) => {  // Add companyFilter prop with default value
    const { hasPermission } = useAuth();

    // State Management
    const [products, setProducts] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // UI State
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showFormModal, setShowFormModal] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [deleteModal, setDeleteModal] = useState({ open: false, product: null });
    const [actionLoading, setActionLoading] = useState(false);

    // Filter State
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCompany, setSelectedCompany] = useState('ALL');
    const [selectedType, setSelectedType] = useState('ALL');
    const [activeFilter, setActiveFilter] = useState('ALL');

    // Fetch initial data
    useEffect(() => {
        fetchInitialData();
    }, []);

    // Update selectedCompany when companyFilter changes
    useEffect(() => {
        if (companyFilter) {
            setSelectedCompany(companyFilter);
        } else {
            setSelectedCompany('ALL');
        }
    }, [companyFilter]);

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            setError('');

            // Fetch both products and companies in parallel
            const [productsResponse, companiesResponse] = await Promise.all([
                productService.getAll(),
                companyService.getAll()
            ]);

            if (productsResponse.success && productsResponse.data) {
                setProducts(productsResponse.data);
            } else {
                throw new Error(productsResponse.message || 'Failed to load products');
            }

            if (companiesResponse.success && companiesResponse.data) {
                setCompanies(companiesResponse.data);
            }
        } catch (err) {
            console.error('Error fetching data:', err);
            setError(err.message || 'Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const fetchProducts = async () => {
        try {
            const response = await productService.getAll();
            if (response.success && response.data) {
                setProducts(response.data);
            }
        } catch (err) {
            console.error('Error fetching products:', err);
            setError(err.message || 'Failed to refresh products');
        }
    };

    // CRUD Operations
    const handleCreateProduct = async (productData) => {
        try {
            setActionLoading(true);
            const response = await productService.create(productData);

            if (response.success) {
                await fetchProducts();
                setShowFormModal(false);
                setEditingProduct(null);
            } else {
                throw new Error(response.message || 'Failed to create product');
            }
        } catch (err) {
            console.error('Error creating product:', err);
            setError(err.message || 'Failed to create product');
        } finally {
            setActionLoading(false);
        }
    };

    const handleUpdateProduct = async (productData) => {
        if (!editingProduct) return;

        try {
            setActionLoading(true);
            const response = await productService.update(editingProduct.id, productData);

            if (response.success) {
                await fetchProducts();
                setShowFormModal(false);
                setEditingProduct(null);
            } else {
                throw new Error(response.message || 'Failed to update product');
            }
        } catch (err) {
            console.error('Error updating product:', err);
            setError(err.message || 'Failed to update product');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteProduct = async () => {
        if (!deleteModal.product) return;

        try {
            setActionLoading(true);
            const response = await productService.delete(deleteModal.product.id);

            if (response.success) {
                await fetchProducts();
                setDeleteModal({ open: false, product: null });
                if (selectedProduct?.id === deleteModal.product.id) {
                    setSelectedProduct(null);
                }
            } else {
                throw new Error(response.message || 'Failed to delete product');
            }
        } catch (err) {
            console.error('Error deleting product:', err);
            setError(err.message || 'Failed to delete product');
        } finally {
            setActionLoading(false);
        }
    };

    // Filter products - Fixed to properly handle companyFilter
    const filteredProducts = products.filter(product => {
        const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            product.code.toLowerCase().includes(searchTerm.toLowerCase());

        // Fixed company matching logic
        const matchesCompany = companyFilter
            ? product.companyId === companyFilter  // If filtered by company from navigation
            : (selectedCompany === 'ALL' || product.companyId === selectedCompany);  // Otherwise use dropdown

        const matchesType = selectedType === 'ALL' || product.type === selectedType;
        const matchesActive = activeFilter === 'ALL' ||
            (activeFilter === 'ACTIVE' && product.active) ||
            (activeFilter === 'INACTIVE' && !product.active);

        return matchesSearch && matchesCompany && matchesType && matchesActive;
    });

    // UI Handlers
    const handleAddProduct = () => {
        setEditingProduct(null);
        setShowFormModal(true);
    };

    const handleEditProduct = (product) => {
        setEditingProduct(product);
        setShowFormModal(true);
        setSelectedProduct(null);
    };

    const handleViewProduct = (product) => {
        setSelectedProduct(product);
    };

    // Show product details view if selected
    if (selectedProduct) {
        const company = companies.find(c => c.id === selectedProduct.companyId);
        return (
            <ProductDetailsView
                product={selectedProduct}
                company={company}
                onBack={() => setSelectedProduct(null)}
                onEdit={handleEditProduct}
            />
        );
    }

    // Show loading state
    if (loading) {
        return <LoadingSpinner message="Loading products..." />;
    }

    // Main products list view
    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-semibold text-gray-900">Products</h2>
                    <p className="text-gray-600 mt-1">
                        {filteredProducts.length} of {products.length} products
                    </p>
                </div>
                {hasPermission('PRODUCTS:CREATE') && (
                    <button
                        onClick={handleAddProduct}
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition flex items-center gap-2"
                    >
                        <Plus className="w-5 h-5" />
                        Add Product
                    </button>
                )}
            </div>

            {/* Error Alert */}
            {error && <ErrorAlert error={error} onRetry={fetchInitialData} />}

            {/* Filters - Pass companyFilter prop and disable handler */}
            <ProductFilters
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                selectedCompany={companyFilter || selectedCompany}
                onCompanyChange={companyFilter ? () => {} : setSelectedCompany}  // Disable when filtered
                selectedType={selectedType}
                onTypeChange={setSelectedType}
                activeFilter={activeFilter}
                onActiveFilterChange={setActiveFilter}
                companies={companies}
                isDisabled={!!companyFilter}  // Pass disabled state
            />

            {/* Products List */}
            <ProductsList
                products={filteredProducts}
                companies={companies}
                onEdit={handleEditProduct}
                onDelete={(product) => setDeleteModal({ open: true, product })}
                onView={handleViewProduct}
                loading={loading}
                hasPermission={hasPermission}
            />

            {/* Product Form Modal */}
            <ProductFormModal
                isOpen={showFormModal}
                onClose={() => {
                    setShowFormModal(false);
                    setEditingProduct(null);
                }}
                onSubmit={editingProduct ? handleUpdateProduct : handleCreateProduct}
                companies={companies}
                product={editingProduct}
                loading={actionLoading}
            />

            {/* Delete Confirmation Modal */}
            <DeleteModal
                isOpen={deleteModal.open}
                onClose={() => setDeleteModal({ open: false, product: null })}
                onConfirm={handleDeleteProduct}
                itemName={deleteModal.product?.name}
                itemType="product"
                loading={actionLoading}
            />
        </div>
    );
};

export default ProductsContent;
